import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { Direction2D, Line2D, Point2D, Point3D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { EntitySnapTarget } from '../layout-snapping/EntitySnapTarget';
import {
  ITransformation,
  ITransformer,
  MirrorTransformation,
  MovementTransformation,
  RotationTransformation,
} from './types';

/**
 * Creates and returns an `ITransformer` instance from the given `ITransformation` blueprint.
 */
export function prepareTransformation(transformation: ITransformation): ITransformer {
  switch (transformation.type) {
    case 'movement':
      return prepareMovementTransformation(transformation);

    case 'rotation':
      return prepareRotationTransformation(transformation);

    case 'mirror':
      return prepareMirrorTransformation(transformation);

    default:
      assertExhaustiveSwitch(transformation);
  }
}

function prepareMovementTransformation(transformation: MovementTransformation): ITransformer {
  const origin = Point3D.fromObject(transformation.origin);
  const destination = Point3D.fromObject(transformation.destination);

  const translationVector = destination.subtract(origin);

  return {
    applyToLevelGuid(guid) {
      return transformation.reassign ? transformation.reassign.levelGuid : guid;
    },

    applyToFeaturePatternContainerGuid(envelope) {
      return envelope.definitionType === 'FeaturePattern' &&
        transformation.reassign &&
        transformation.reassign.featurePatternContainerGuid !== undefined
        ? transformation.reassign.featurePatternContainerGuid
        : envelope.featurePatternContainerGuid;
    },

    applyToPoint(point) {
      return Point2D.fromObject(point).add(translationVector).toObject();
    },

    applyToLine(line) {
      return {
        basePoint: this.applyToPoint(line.basePoint),
        direction: line.direction,
      };
    },

    applyToElevation(elevation) {
      return elevation + translationVector.z;
    },

    applyToRotation: NOT_REQUIRED,
    applyToJustification: NOT_REQUIRED,
    applyToHorizontalAnchor: NOT_REQUIRED,
  };
}

function prepareRotationTransformation(transformation: RotationTransformation): ITransformer {
  const center = Point2D.fromObject(transformation.center);

  return {
    applyToLevelGuid(guid) {
      return transformation.reassign ? transformation.reassign.levelGuid : guid;
    },

    applyToFeaturePatternContainerGuid(envelope) {
      return envelope.definitionType === 'FeaturePattern' &&
        transformation.reassign &&
        transformation.reassign.featurePatternContainerGuid !== undefined
        ? transformation.reassign.featurePatternContainerGuid
        : envelope.featurePatternContainerGuid;
    },

    applyToPoint(point: Point2DInterface): Point2DInterface {
      return Point2D.fromObject(point).rotateAround(center, transformation.angle).toObject();
    },

    applyToLine(line) {
      const direction = Direction2D.fromObject(line.direction);

      return {
        basePoint: this.applyToPoint(line.basePoint),
        direction: direction.rotate(transformation.angle),
      };
    },

    applyToElevation: NOT_REQUIRED,

    applyToRotation(angle: number): number {
      return (angle + transformation.angle) % 360;
    },

    applyToJustification: NOT_REQUIRED,
    applyToHorizontalAnchor: NOT_REQUIRED,
  };
}

function prepareMirrorTransformation(transformation: MirrorTransformation): ITransformer {
  const mirrorLine = EntitySnapTarget.fromObject(transformation.center).getDefiningLine();
  const mirrorLineDirection = mirrorLine.direction;

  function mirrorPoint(point: Point2D): Point2D {
    const projectedPoint = point.projectOntoLine(mirrorLine);
    const vector = projectedPoint.subtract(point);

    return projectedPoint.add(vector);
  }

  return {
    applyToLevelGuid(guid) {
      return transformation.reassign ? transformation.reassign.levelGuid : guid;
    },

    applyToFeaturePatternContainerGuid(envelope) {
      return envelope.definitionType === 'FeaturePattern' &&
        transformation.reassign &&
        transformation.reassign.featurePatternContainerGuid !== undefined
        ? transformation.reassign.featurePatternContainerGuid
        : envelope.featurePatternContainerGuid;
    },

    applyToPoint(pointInterface) {
      return mirrorPoint(Point2D.fromObject(pointInterface)).toObject();
    },

    applyToLine(line) {
      const basePoint = Point2D.fromObject(line.basePoint);
      const reflectedBasePoint = mirrorPoint(basePoint);

      const endPoint = basePoint.add(line.direction);
      const reflectedEndPoint = mirrorPoint(endPoint);

      const reflectedDirection = Direction2D.fromPoints(reflectedBasePoint, reflectedEndPoint);

      return new Line2D(reflectedBasePoint, reflectedDirection).toObject();
    },

    applyToElevation: NOT_REQUIRED,

    applyToRotation(angle) {
      // This rotation mirroring logic is non-standard because we have to account for mirroring the anchors too. We've
      //   chosen to mirror the horizontal anchor and preserve the vertical anchor, which results in the formula below.
      //
      // Note that if we ever support objects with a rotation property but no horizontal/vertical anchor properties,
      //   or vice versa, this mirroring logic will not work.
      return (2 * mirrorLineDirection.angle - angle + 900) % 360;
    },

    applyToJustification(value) {
      if (value === 'Front') return 'Back';
      if (value === 'Back') return 'Front';

      return value;
    },

    applyToHorizontalAnchor(anchor) {
      if (anchor === 'Left') return 'Right';
      if (anchor === 'Right') return 'Left';

      return anchor;
    },
  };
}

const NOT_REQUIRED = <T>(value: T): T => value;
